import API from '../../../APIs/API';
import {ParameterStore} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {TemplateDataFields} from '../../ContactForm/EmailEditor/EmailEditor';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/QuoteXref';
import Utils from '../../../Core/Utils';

// Helper function to get API code from parameter value
function getApiCode(paramId: string, value: any): string {
    if (fieldOptions.hasOwnProperty(paramId)) {
        const fieldOption = fieldOptions[paramId as keyof typeof fieldOptions] as any;
        if (fieldOption.optionValues) {
            const index = fieldOption.options.findIndex((o: string) => Utils.MakeVarName(o) === value);
            if (index > -1) {
                return fieldOption.optionValues[index];
            }
        }
    }
    return value?.toString() || '';
}

export const tafeAPI = async (parameterStore: ParameterStore, formType?: 'single-quote' | 'combination-quote') => {
    let quotes = parameterStore.quote;
    if (formType === 'combination-quote' && parameterStore.quoteCombination) {
        quotes = parameterStore.quoteCombination;
    }

    // Fetch info from form
    const age = parameterStore.prequal.age.value;
    const gender = parameterStore.approval.gender.value;
    const state = parameterStore.approval.state.value;
    const healthRatingTAFE = getApiCode('healthRatingTAFE', quotes.healthRatingTAFE.value); // 'NonSmoker' or 'Smoker'
    const solveForTAFE = getApiCode('solveForTAFE', quotes.solveForTAFE.value); // 'P' or 'C'
    const specifiedPremiumTAFE = quotes.specifiedPremiumTAFE.value;
    const specifiedFaceAmountTAFE = getApiCode('specifiedFaceAmountTAFE', quotes.specifiedFaceAmountTAFE.value); // numeric string like '10000'

    // TAFE is not available in NY
    if (state === 'NY') {
        return {
            error: 'TA FE Express is not available in New York state.',
        };
    }

    // Load TAFE rate table
    let data: {
        stateId: string;
        genderId: string;
        insuredAge: number;
        faceAmount: number;
        modalPremium: number;
        ratingClass: string;
    }[] = [];

    try {
        data =
            (
                (await API.Call(
                    `v2/internal/quotes/fe?stateId=all&insuredAge=${age}&genderId=${gender}&ratingClass=${healthRatingTAFE}`,
                    'GET',
                    undefined,
                )) as any
            )?.quotes || [];
    } catch (error) {
        return {
            error: 'Failed to retrieve TAFE quotes. Please try again.',
        };
    }

    if (data.length === 0) {
        return {
            error: 'Could not find products with the provided information.',
        };
    }

    let selectedRecord;

    if (solveForTAFE === 'C') {
        // Solve for Coverage (find Face Amount based on Premium)
        const premiumAmount = parseFloat(specifiedPremiumTAFE as string);

        if (isNaN(premiumAmount) || premiumAmount <= 0) {
            return {
                error: 'Please specify a valid premium amount.',
            };
        }

        // Find the record with premium closest to the specified amount
        let closestRecord = data[0];
        let minDiff = Math.abs(data[0].modalPremium - premiumAmount);

        for (const record of data) {
            const diff = Math.abs(record.modalPremium - premiumAmount);
            if (diff < minDiff) {
                minDiff = diff;
                closestRecord = record;
            }
        }

        // Check if the match is reasonably close (within $5)
        if (minDiff > 5) {
            return {
                error: `No exact match found for premium $${premiumAmount.toFixed(2)}. Closest available is $${closestRecord.modalPremium.toFixed(2)} for $${closestRecord.faceAmount.toLocaleString()} coverage.`,
            };
        }

        selectedRecord = closestRecord;
    } else {
        // Solve for Premium (find Premium based on Face Amount)
        // specifiedFaceAmountTAFE is already a numeric string from optionValues (e.g., '10000')
        const faceAmount = parseInt(specifiedFaceAmountTAFE as string, 10);

        if (isNaN(faceAmount) || faceAmount <= 0) {
            return {
                error: 'Please specify a valid face amount.',
            };
        }

        // Find exact match for face amount
        const exactMatch = data.find((record) => record.faceAmount === faceAmount);

        if (!exactMatch) {
            return {
                error: `No rate found for $${faceAmount.toLocaleString()} coverage.`,
            };
        }

        selectedRecord = exactMatch;
    }

    return {
        faceAmount: selectedRecord.faceAmount,
        monthlyPremium: selectedRecord.modalPremium,
        productName: 'TA FE Express',
        out: [
            {
                name: 'Carrier',
                value: 'Transamerica',
                type: 'string',
                fieldName: TemplateDataFields.carrier,
            },
            {
                name: 'Product',
                value: 'TA FE Express',
                type: 'string',
                fieldName: TemplateDataFields.productName,
            },
            {
                name: 'Face Amount',
                value: selectedRecord.faceAmount,
                type: 'currency',
                fieldName: TemplateDataFields.faceAmount,
            },
            {
                name: 'Monthly Premium',
                value: selectedRecord.modalPremium,
                type: 'currency',
                fieldName: TemplateDataFields.monthlyPremium,
            },
            {
                name: 'Risk Class',
                value: healthRatingTAFE === 'NonSmoker' ? 'Non-Smoker' : 'Smoker',
                type: 'string',
            },
            {
                name: 'Valid',
                value: 'valid',
                type: 'string',
            },
        ],
        requestDetails: {
            age,
            gender,
            healthRatingTAFE,
            solveForTAFE,
            ...(solveForTAFE === 'C' ? {specifiedPremiumTAFE} : {specifiedFaceAmountTAFE}),
        },
    };
};
